import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import '../styles/Login.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorType('');

    // Use XMLHttpRequest to avoid fetch body stream issues
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/forgot-password`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setIsLoading(false);
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Unknown error' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        setSubmitted(true);
      } else {
        const msg = data.detail || 'An error occurred';
        
        if (msg.toLowerCase().includes('no account')) {
          setError(msg);
          setErrorType('not_found');
        } else if (msg.toLowerCase().includes('email service') || msg.toLowerCase().includes('not configured')) {
          setError(msg);
          setErrorType('service');
        } else {
          setError(msg);
          setErrorType('general');
        }
      }
    };
    
    xhr.onerror = function() {
      setIsLoading(false);
      setError('Unable to connect to server. Please try again.');
      setErrorType('general');
    };
    
    xhr.send(JSON.stringify({ email }));
  };

  if (submitted) {
    return (
      <div className="login-page">
        <section className="auth-section">
          <div className="container">
            <div className="auth-container">
              <div className="auth-form" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <img src="/cadence-logo-gold.png" alt="CADENCE" style={{ width: '80px', marginBottom: '1.5rem' }} />
                <CheckCircle size={48} color="#D4AF37" style={{ marginBottom: '1rem' }} />
                <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Check Your Email</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  If an account exists for <strong>{email}</strong>, you will receive password reset instructions shortly.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '2rem' }}>
                  Didn&apos;t receive an email? Check your spam folder or contact us at{' '}
                  <a href="mailto:CADENCE@cadencemedicalconsulting.com" style={{ color: 'var(--brand-gold)' }}>
                    CADENCE@cadencemedicalconsulting.com
                  </a>
                </p>
                <Link to="/login" className="btn-primary" style={{ display: 'inline-block' }}>Return to Login</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="login-page">
      <section className="hero-mini" style={{ background: 'linear-gradient(135deg, var(--brand-plum) 0%, #2D0A31 100%)', padding: '4rem 0 3rem', textAlign: 'center' }}>
        <div className="container">
          <img src="/cadence-logo-gold.png" alt="CADENCE Medical Consulting" className="login-logo" style={{ width: '120px' }} />
          <h1 style={{ color: 'var(--brand-gold)', marginBottom: '0.5rem' }}>Reset Password</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>Enter your email to receive reset instructions</p>
        </div>
      </section>

      <section className="auth-section">
        <div className="container">
          <div className="auth-container">
            {error && (
              <div style={{
                background: errorType === 'not_found' ? 'rgba(234, 179, 8, 0.1)' : errorType === 'service' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${errorType === 'not_found' ? '#EAB308' : errorType === 'service' ? '#3B82F6' : '#EF4444'}`,
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <AlertCircle size={20} style={{ color: errorType === 'not_found' ? '#EAB308' : errorType === 'service' ? '#3B82F6' : '#EF4444', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ margin: 0, color: 'var(--text-primary)' }}>{error}</p>
                  {errorType === 'not_found' && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <Link to="/login" style={{ color: 'var(--brand-gold)' }}>Create a new account</Link> or try a different email address.
                    </p>
                  )}
                  {errorType === 'service' && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Please contact <a href="mailto:CADENCE@cadencemedicalconsulting.com" style={{ color: 'var(--brand-gold)' }}>CADENCE@cadencemedicalconsulting.com</a> for password assistance.
                    </p>
                  )}
                </div>
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email Address</label>
                <div className="input-with-icon">
                  <Mail size={20} />
                  <input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-password-email" />
                </div>
              </div>

              <button type="submit" className="btn-primary auth-submit" disabled={isLoading} data-testid="forgot-password-submit">
                {isLoading ? 'Sending...' : 'Send Reset Instructions'}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--brand-gold)', textDecoration: 'none', fontSize: '0.9375rem' }}>
                  <ArrowLeft size={18} />
                  Back to Login
                </Link>
              </div>
            </form>

            <div className="auth-divider"><span>Need help?</span></div>

            <div className="auth-contact">
              <p>
                Contact us at <a href="mailto:CADENCE@cadencemedicalconsulting.com">CADENCE@cadencemedicalconsulting.com</a> or call <a href="tel:+12232520409">(223) 252-0409</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPassword;
